/**
 * Firebase initialization + social sign-in helpers.
 *
 * Ports the behavior of the old web app's `firebaseAuth.js` / `SocialLogin.jsx`
 * to TypeScript. Initializes a single Firebase app from the `VITE_FIREBASE_*`
 * env vars and exposes `signInWithGoogle()` / `signInWithFacebook()` that run a
 * popup flow and return the normalized profile fields the backend expects.
 *
 * Guards against missing config: if `VITE_FIREBASE_API_KEY` is empty the module
 * still loads (the app shouldn't crash), but calling a sign-in helper throws a
 * clear "Firebase not configured" error.
 *
 * Bundle policy — Firebase Auth + Messaging together weigh ~30–60 kB gzipped
 * and are needed only for: (a) the Google / Facebook / Twitter sign-in popup
 * on /login + /register and (b) browser push registration from
 * `PermissionPrompts`. To keep that weight off the main bundle (which loads
 * on every page), all `firebase/*` modules are loaded via dynamic
 * `import()` inside the helpers below. Type-only imports stay at the top
 * (TS erases them at build time, so they don't influence the runtime graph).
 * The public API (`signInWithGoogle()`, `signInWithFacebook()`,
 * `signInWithTwitter()`, `firebaseLogout()`, `isFirebaseConfigured`,
 * `getMessagingToken()`, plus the `SocialProvider` / `FirebaseProfile`
 * types) is unchanged — callers see no difference except the first call
 * pays the network/parse cost.
 */
import type { FirebaseApp } from 'firebase/app';
import type { Auth, UserCredential } from 'firebase/auth';
import type { Messaging } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  // Web push needs the sender id; everything else above is auth-only.
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
};

/** Optional VAPID public key — Firebase web push requires this for getToken(). */
const FCM_VAPID_KEY: string | undefined = import.meta.env.VITE_FIREBASE_VAPID_KEY;

/** Whether the build was given a usable Firebase config. */
export const isFirebaseConfigured = Boolean(firebaseConfig.apiKey);

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;

/**
 * Lazily initialize (or reuse) the Firebase app + auth instance.
 * Dynamically imports `firebase/app` + `firebase/auth` on first call so
 * those modules stay out of the main bundle. Throws if Firebase env vars
 * are absent so callers get a clear error instead of an opaque SDK
 * failure.
 */
async function ensureAuth(): Promise<Auth> {
  if (!isFirebaseConfigured) {
    throw new Error(
      'Firebase not configured: set VITE_FIREBASE_API_KEY (and related VITE_FIREBASE_* env vars).',
    );
  }
  if (!_app) {
    const { initializeApp, getApps, getApp } = await import('firebase/app');
    _app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
  }
  if (!_auth) {
    const { getAuth } = await import('firebase/auth');
    _auth = getAuth(_app);
  }
  return _auth;
}

/** Provider identifiers accepted by the backend `loginwith/:provider` route. */
export type SocialProvider = 'google' | 'facebook' | 'twitter';

/**
 * Normalized profile returned by the sign-in helpers — the shape the backend
 * `auth/loginwith` endpoint consumes (snake_case, split name, Firebase token).
 */
export interface FirebaseProfile {
  /** Provider name without the `.com` suffix (`google`, `facebook`, `twitter`). */
  provider: SocialProvider;
  /** Provider-specific unique id (Firebase uid), used in the URL path. */
  identifier: string;
  firebase_id: string;
  firstname: string;
  lastname: string;
  email: string | null;
  phone: string | null;
  photourl: string | null;
  emailverify: 0 | 1;
  /** Firebase ID token (verified server-side). */
  accessToken: string;
}

/**
 * Split a Firebase `displayName` into first / last name, matching the old
 * app's logic (first word → firstname, remainder → lastname).
 */
function splitName(displayName: string | null): { firstname: string; lastname: string } {
  const name = (displayName ?? '').trim();
  if (!name) return { firstname: '', lastname: '' };
  const idx = name.indexOf(' ');
  if (idx === -1) return { firstname: name, lastname: '' };
  return {
    firstname: name.slice(0, idx),
    lastname: name.slice(idx + 1).trim(),
  };
}

/** Map a successful popup credential to the backend profile payload. */
async function toProfile(
  provider: SocialProvider,
  result: UserCredential,
): Promise<FirebaseProfile> {
  const user = result.user;
  const { firstname, lastname } = splitName(user.displayName);
  const accessToken = await user.getIdToken();

  // Firebase surfaces Twitter's email on `user.providerData[0].email`, NOT
  // on the top-level `user.email` (which stays null unless Twitter\'s
  // email is verified AND linked as a primary). Facebook has the same
  // behavior when the app is approved for `email` but the FB user
  // authorised it only for the current session. Fall through the linked
  // provider identities to pick up the address the backend needs to key
  // account lookups on. Same fallback for phone.
  const providerEmail = user.providerData.find((p) => p?.email)?.email ?? null;
  const providerPhone = user.providerData.find((p) => p?.phoneNumber)?.phoneNumber ?? null;

  return {
    provider,
    identifier: user.uid,
    firebase_id: user.uid,
    firstname,
    lastname,
    email: user.email ?? providerEmail,
    phone: user.phoneNumber ?? providerPhone,
    photourl: user.photoURL ?? null,
    emailverify: user.emailVerified ? 1 : 0,
    accessToken,
  };
}

/** Run the Google popup sign-in and return the normalized profile. */
export async function signInWithGoogle(): Promise<FirebaseProfile> {
  const [{ signInWithPopup, GoogleAuthProvider }, authInstance] = await Promise.all([
    import('firebase/auth'),
    ensureAuth(),
  ]);
  const provider = new GoogleAuthProvider();
  provider.addScope('profile');
  provider.addScope('email');
  const result = await signInWithPopup(authInstance, provider);
  return toProfile('google', result);
}

/** Run the Facebook popup sign-in and return the normalized profile. */
export async function signInWithFacebook(): Promise<FirebaseProfile> {
  const [{ signInWithPopup, FacebookAuthProvider }, authInstance] = await Promise.all([
    import('firebase/auth'),
    ensureAuth(),
  ]);
  const provider = new FacebookAuthProvider();
  provider.addScope('email');
  provider.addScope('public_profile');
  // Force a re-prompt so Facebook re-shares email even if previously denied.
  provider.setCustomParameters({ auth_type: 'rerequest' });
  const result = await signInWithPopup(authInstance, provider);
  return toProfile('facebook', result);
}

/** Run the Twitter (X) popup sign-in and return the normalized profile. */
export async function signInWithTwitter(): Promise<FirebaseProfile> {
  const [{ signInWithPopup, TwitterAuthProvider }, authInstance] = await Promise.all([
    import('firebase/auth'),
    ensureAuth(),
  ]);
  const provider = new TwitterAuthProvider();
  const result = await signInWithPopup(authInstance, provider);
  return toProfile('twitter', result);
}

/** Sign the user out of Firebase (best-effort; no-op if not configured). */
export async function firebaseLogout(): Promise<void> {
  if (!isFirebaseConfigured || !_auth) return;
  try {
    // `_auth` was populated only after a prior `ensureAuth()` succeeded, so
    // the firebase/auth module is guaranteed to be in the cache — but we
    // still go through the dynamic import to avoid pulling it into the
    // dependency graph of any module that statically references this
    // function.
    const { signOut } = await import('firebase/auth');
    await signOut(_auth);
  } catch {
    /* best-effort */
  }
}

/* -------------------------------------------------------------------------- */
/* Cloud Messaging (web push)                                                  */
/* -------------------------------------------------------------------------- */

let _messaging: Messaging | null = null;

/**
 * Lazily initialize Firebase Cloud Messaging on top of the shared app.
 * Returns `null` when the browser doesn't support service-worker push or when
 * Firebase env vars are missing — callers should treat null as "skip push".
 *
 * Dynamically imports `firebase/messaging` so the ~10 kB push module only
 * loads when the user actually opts into notifications.
 */
async function ensureMessaging(): Promise<Messaging | null> {
  if (!isFirebaseConfigured) return null;
  const { getMessaging, isSupported } = await import('firebase/messaging');
  if (!(await isSupported())) return null;
  if (!_app) {
    const { initializeApp, getApps, getApp } = await import('firebase/app');
    _app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
  }
  if (!_messaging) {
    _messaging = getMessaging(_app);
  }
  return _messaging;
}

/**
 * Request an FCM registration token for this browser. Mirrors the legacy
 * `getFirebaseToken(setToken)` helper in `REACTJS/src/app/services/firebase.js`
 * but returns a promise instead of taking a callback.
 *
 * Returns the token string on success, or `null` when:
 *   - Firebase isn't configured
 *   - the browser doesn't support web push (Safari iOS < 16, etc.)
 *   - notification permission is denied
 *   - the service worker isn't registered or token retrieval fails
 *
 * Caller must have already prompted the user for `Notification.requestPermission()`
 * — this helper does NOT prompt (so the UI controls when the OS dialog appears).
 */
export async function getMessagingToken(): Promise<string | null> {
  try {
    if (typeof Notification === 'undefined') return null;
    if (Notification.permission !== 'granted') return null;
    const messaging = await ensureMessaging();
    if (!messaging) return null;
    const { getToken } = await import('firebase/messaging');
    const token = await getToken(messaging, {
      vapidKey: FCM_VAPID_KEY,
    });
    return token || null;
  } catch {
    /* token retrieval can fail when the SW isn't registered or the VAPID key
     * is missing/invalid — treat as soft failure so callers can move on. */
    return null;
  }
}
